import { useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { useMemo, useState } from 'react'
import { Link } from 'react-router'
import type { RouterOutputs } from 'shared/api'
import { trpc } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import { PageFrame } from 'shared/components/page-frame'
import { PointSelector } from 'shared/components/point-selector'
import { SectionTitle } from 'shared/components/section'
import { Badge } from 'shared/components/ui/badge'
import { Button } from 'shared/components/ui/button'
import { useDataTable } from 'shared/hooks/use-data-table'
import { useTranslate } from 'shared/lib/i18n'
import { formatDayTime } from 'shared/lib/utils/date'
import { useAuthStore } from 'shared/store/auth'
import { useLocationScope } from 'shared/store/location-scope'
import { DisposeAct } from './DisposeAct'

/**
 * Storage on the Point's shelf (ADR-0033, ticket 40): what the operator does with
 * the shelf today. First what is theirs to do here — dispose of what the partner
 * decided to dispose of (ticket 42); then what expires soon, while it can still be
 * handed over and the Recipient reminded; then what is held — waiting for the
 * partner's decision, or going back by the driver's pickup list. Expiry is DERIVED,
 * never stored (CONTEXT — Storage Clock): the backend computes it. A Parcel in the
 * last three lists is not handed over.
 *
 * The Point comes from `useLocationScope`: an operator works the Point their account
 * is bound to (ADR-0029) and sees no picker; an admin picks one in the row under the
 * title. A Parcel's code opens its card.
 */

type ExpiryLists = RouterOutputs['storage']['ExpiringAtPoint']
type ExpiryParcel = ExpiryLists['expiringSoon'][number]
/** One empty list for every render — a table given a new array each time resets itself in a loop. */
const NONE: ExpiryParcel[] = []

export function StorageExpiry() {
	const { t } = useTranslate()
	const { canPick, locationId } = useLocationScope()

	return (
		<PageFrame title='nav.storageExpiry'>
			{/* The operator's Point is their own and goes unsaid; one with no Point is told so here. */}
			{(canPick || !locationId) && (
				<PointSelector
					id='expiry-point'
					label={t('storageExpiry.pointLabel')}
					placeholder={t('storageExpiry.pointPlaceholder')}
					className='flex items-center gap-2'
				/>
			)}
			{locationId ? (
				<ExpiryListsView locationId={locationId} />
			) : (
				canPick && (
					<p className='text-sm text-muted-foreground' data-testid='expiry-need-point'>
						{t('storageExpiry.needPoint')}
					</p>
				)
			)}
		</PageFrame>
	)
}

function ExpiryListsView({ locationId }: { locationId: string }) {
	const { t } = useTranslate()
	// Only the Point's operator disposes, at their own counter (ADR-0029).
	const mayDispose = useAuthStore((s) => s.user.role) === 'operator'
	const [disposing, setDisposing] = useState<ExpiryParcel | null>(null)
	const query = useQuery(trpc.storage.ExpiringAtPoint.queryOptions({ destinationPointId: locationId }))

	if (query.isLoading) return <p className='text-sm text-muted-foreground'>{t('storageExpiry.loading')}</p>
	if (query.isError) {
		return (
			<p className='text-sm text-destructive' data-testid='expiry-error'>
				{t('storageExpiry.loadFailed', { message: query.error.message })}
			</p>
		)
	}

	const lists = query.data
	return (
		<div className='flex flex-col gap-8'>
			<ExpirySection
				testId='expiry-to-dispose'
				title={t('storageExpiry.toDisposeTitle')}
				hint={t(mayDispose ? 'storageExpiry.toDisposeHint' : 'storageExpiry.toDisposeHintAdmin')}
				emptyText={t('storageExpiry.toDisposeEmpty')}
				rows={lists?.toDispose ?? NONE}
				onDispose={mayDispose ? setDisposing : undefined}
			/>
			<ExpirySection
				testId='expiry-expiring-soon'
				title={t('storageExpiry.expiringSoonTitle')}
				hint={t('storageExpiry.expiringSoonHint')}
				emptyText={t('storageExpiry.expiringSoonEmpty')}
				rows={lists?.expiringSoon ?? NONE}
			/>
			<ExpirySection
				testId='expiry-awaiting'
				title={t('storageExpiry.awaitingTitle')}
				hint={t('storageExpiry.awaitingHint')}
				emptyText={t('storageExpiry.awaitingEmpty')}
				rows={lists?.awaitingDecision ?? NONE}
				showAnswerBy
			/>
			<ExpirySection
				testId='expiry-to-return'
				title={t('storageExpiry.toReturnTitle')}
				hint={t('storageExpiry.toReturnHint')}
				emptyText={t('storageExpiry.toReturnEmpty')}
				rows={lists?.toReturn ?? NONE}
			/>

			{disposing && (
				<DisposeAct
					parcelId={disposing.parcelId}
					code={disposing.trackingCode}
					onCancel={() => setDisposing(null)}
					onDone={() => {
						setDisposing(null)
						void query.refetch()
					}}
				/>
			)}
		</div>
	)
}

/** One list of the shelf: its title with the count and what it means behind an «i», then its table. */
function ExpirySection({
	testId,
	title,
	hint,
	emptyText,
	rows,
	showAnswerBy = false,
	onDispose,
}: {
	testId: string
	title: string
	hint: string
	emptyText: string
	rows: ExpiryParcel[]
	/** The deadline of the partner's answer — for the parcels waiting for it. */
	showAnswerBy?: boolean
	/** Given only to the Point's operator, on the list to dispose of (ticket 42). */
	onDispose?: (row: ExpiryParcel) => void
}) {
	const { t } = useTranslate()
	const columns = useMemo<ColumnDef<ExpiryParcel>[]>(() => {
		const cols: ColumnDef<ExpiryParcel>[] = [
			{
				id: 'code',
				header: t('storageExpiry.colTrackingCode'),
				// The same widths in all four tables, so their columns line up down the page.
				meta: { headClassName: 'w-[28%]' },
				cell: ({ row }) => (
					<div className='flex flex-wrap items-center gap-2'>
						<Link to={`/parcels/${row.original.parcelId}`} className='font-mono hover:underline'>
							{row.original.trackingCode}
						</Link>
						{/* Found after it was declared lost: its partner is asked to deliver or return it (ticket 43). */}
						{row.original.decisionKind === 'found' && (
							<Badge
								variant='outline'
								className='border-amber-500/60 text-amber-700 dark:text-amber-400'
								data-testid={`expiry-found-${row.original.parcelId}`}
							>
								{t('storageExpiry.foundLost')}
							</Badge>
						)}
					</div>
				),
			},
			{
				id: 'recipient',
				header: t('storageExpiry.colRecipient'),
				meta: { headClassName: 'w-[30%]' },
				cell: ({ row }) => (
					<span>
						{row.original.recipient.name}{' '}
						<span className='text-muted-foreground'>{row.original.recipient.clientRef}</span>
					</span>
				),
			},
			{
				id: 'expiresAt',
				header: t('storageExpiry.colExpiresAt'),
				// The last free column takes the rest of the width, so the ones before it keep theirs.
				meta: showAnswerBy ? { headClassName: 'w-[20%]' } : undefined,
				cell: ({ row }) =>
					row.original.expiresAt ? (
						<span className='tabular-nums'>{formatDayTime(row.original.expiresAt)}</span>
					) : (
						<span className='text-muted-foreground'>{t('storageExpiry.neverExpires')}</span>
					),
			},
		]
		if (showAnswerBy) {
			cols.push({
				id: 'answerBy',
				header: t('storageExpiry.colAnswerBy'),
				cell: ({ row }) =>
					row.original.answerBy ? (
						<span className='tabular-nums' data-testid='expiry-answer-by'>
							{formatDayTime(row.original.answerBy)}
						</span>
					) : (
						<span className='text-muted-foreground' data-testid='expiry-answer-by'>
							{t('storageExpiry.answerByPending')}
						</span>
					),
			})
		}
		if (onDispose) {
			cols.push({
				id: 'actions',
				header: '',
				meta: { cellClassName: 'w-0 text-right' },
				cell: ({ row }) => (
					<Button
						size='sm'
						variant='destructive'
						data-testid={`expiry-dispose-${row.original.parcelId}`}
						onClick={() => onDispose(row.original)}
					>
						{t('disposal.open')}
					</Button>
				),
			})
		}
		return cols
	}, [t, showAnswerBy, onDispose])

	const { table } = useDataTable({
		data: rows,
		columns,
		rowCount: rows.length,
		manualPagination: false,
		manualSorting: false,
		manualFiltering: false,
		enableRowSelection: false,
		getRowId: (p) => p.parcelId,
		initialState: { pagination: { pageIndex: 0, pageSize: 1000 } },
	})

	return (
		<section className='flex flex-col gap-2' data-testid={testId}>
			<SectionTitle title={title} count={rows.length} hint={hint} />
			<DataTable
				table={table}
				withPagination={false}
				maxHeightClassName='max-h-none'
				emptyText={<span data-testid={`${testId}-empty`}>{emptyText}</span>}
				row={{ attributes: (p) => ({ 'data-testid': `expiry-row-${p.parcelId}` }) }}
			/>
		</section>
	)
}
